'use client';

import { Fragment, useEffect, useMemo, useState } from 'react';
import { sanitizeNavigationUrl } from '@bridge/utils';
import { useSearchParams } from 'next/navigation';
import { twMerge } from 'tailwind-merge';

import { Body } from '../typography/Body';
import { FlexContainer } from './FlexContainer';
import { Separator } from './Separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './Tabs';

import type { TabsOrientation } from './Tabs';
import type { FilterOption } from '@bridge/types';
import type { ReactElement, ReactNode } from 'react';

export interface TabTriggerConfig extends FilterOption<string> {
  /** Renders the trigger dimmed and unselectable; it is skipped as the default and rejected from the URL. Because defaultTab depends on which tabs are disabled, a param-less URL can open different tabs for different customers. */
  disabled?: boolean;
  /** Shown beneath the label while the trigger is disabled. */
  disabledReason?: string;
}

type PropsOrFunction<TProps> = TProps | (() => TProps);

export interface TabConfig<TProps = Record<string, unknown>> {
  tabTrigger: TabTriggerConfig;
  Component: React.ComponentType<TProps> | ReactNode;
  props?: PropsOrFunction<TProps>;
  /** Return false to hide this tab. */
  canShow?: () => boolean;
  /** Rendered on the tab list row, to the right of the triggers, while this tab is selected. */
  headerActions?: ReactNode;
  /** Vertical orientation only: render a divider above this tab's trigger. */
  separatorBefore?: boolean;
}

interface ConfigurableTabsProps<TProps = Record<string, unknown>> {
  tabs: TabConfig<TProps>[];
  defaultValue?: string;
  className?: string;
  deepLink?: boolean;
  queryParamName?: string;
  /**
   * Clears search params on tab change. `true` clears every other param, for tabs hosting
   * independent content with their own filters/pagination. A predicate clears only the params
   * it matches, for tabs nested under another deep-linked control whose param must survive.
   */
  clearSearchParamsOnChange?: boolean | ((param: string) => boolean);
  /** Called with the newly selected tab value. Fires whether or not deep linking is enabled. */
  onValueChange?: (value: string) => void;
  orientation?: TabsOrientation;
}

interface TriggerLabelProps {
  tabTrigger: TabTriggerConfig;
}

function TriggerLabel({ tabTrigger }: TriggerLabelProps): ReactElement {
  if (!tabTrigger.disabled || !tabTrigger.disabledReason) return <>{tabTrigger.label}</>;
  return (
    <FlexContainer className="min-w-0 flex-1 gap-0.5" direction="column" items="stretch">
      <Body size="sm">{tabTrigger.label}</Body>
      <Body className="text-content-tertiary" size="xs">
        {tabTrigger.disabledReason}
      </Body>
    </FlexContainer>
  );
}

/** Returns `value` when it names an enabled tab, otherwise undefined. */
function resolveSelectableValue<TProps>(
  tabs: TabConfig<TProps>[],
  value: string | null | undefined
): string | undefined {
  return value && tabs.some((tab) => tab.tabTrigger.value === value && !tab.tabTrigger.disabled) ? value : undefined;
}

function resolveProps<TProps>(props: PropsOrFunction<TProps>): TProps {
  return typeof props === 'function' ? (props as () => TProps)() : props;
}

export default function ConfigurableTabs<TProps = Record<string, unknown>>({
  tabs,
  defaultValue,
  className,
  deepLink = false,
  queryParamName = 'tab',
  clearSearchParamsOnChange = false,
  onValueChange,
  orientation = 'horizontal',
}: ConfigurableTabsProps<TProps>): ReactElement {
  const searchParams = useSearchParams();

  // Filter tabs based on canShow callbacks
  const visibleTabTriggers = useMemo(
    () =>
      tabs.filter((tab) => {
        if (!tab.canShow) return true;
        return tab.canShow();
      }),
    [tabs]
  );

  const firstSelectableValue = visibleTabTriggers.find((tab) => !tab.tabTrigger.disabled)?.tabTrigger.value;
  const defaultTab = resolveSelectableValue(visibleTabTriggers, defaultValue) ?? firstSelectableValue;

  // Selected tab; drives the controlled Tabs value in both modes
  const [selectedTab, setSelectedTab] = useState<string | undefined>(() => {
    if (!deepLink) return defaultTab;
    // Read from URL on initial render
    const urlTab = searchParams?.get(queryParamName);
    return resolveSelectableValue(visibleTabTriggers, urlTab) ?? defaultTab;
  });

  // Sync selected tab with URL when deep linking is enabled
  useEffect(() => {
    if (!deepLink) return;

    const urlTab = searchParams?.get(queryParamName);
    const selectableUrlTab = resolveSelectableValue(visibleTabTriggers, urlTab);

    // If URL has a selectable tab param, use it
    if (selectableUrlTab) {
      setSelectedTab(selectableUrlTab);
    }
    // If URL has no tab param, use the default
    else if (!urlTab) {
      setSelectedTab(defaultTab);
    }
  }, [deepLink, searchParams, queryParamName, visibleTabTriggers, defaultTab]);

  const handleTabChange = (value: string): void => {
    setSelectedTab(value);
    onValueChange?.(value);

    if (!deepLink) return;

    // Get current URL
    const currentUrl = window.location.pathname + window.location.search;
    const url = new URL(currentUrl, window.location.origin);

    if (clearSearchParamsOnChange) {
      const shouldClear = clearSearchParamsOnChange === true ? () => true : clearSearchParamsOnChange;
      const keys = [...url.searchParams.keys()];
      for (const key of keys) {
        if (key !== queryParamName && shouldClear(key)) url.searchParams.delete(key);
      }
    }

    // If selecting the default tab, remove the query param
    if (value === defaultTab) {
      url.searchParams.delete(queryParamName);
    } else {
      url.searchParams.set(queryParamName, value);
    }

    window.history.replaceState(null, '', sanitizeNavigationUrl(url.pathname + url.search));
  };

  const activeTab = resolveSelectableValue(visibleTabTriggers, selectedTab) ?? defaultTab;

  // Controlled in both modes: a selection that becomes disabled must fall back to defaultTab.
  const tabsProps = { value: activeTab, onValueChange: handleTabChange };

  const headerActions = visibleTabTriggers.find((tab) => tab.tabTrigger.value === activeTab)?.headerActions;

  const tabContents = tabs.map((tab) => {
    const componentProps = resolveProps(tab.props);
    const isReactNode = typeof tab.Component !== 'function';
    return (
      <TabsContent key={tab.tabTrigger.value} value={tab.tabTrigger.value}>
        {isReactNode
          ? (tab.Component as ReactNode)
          : (() => {
              const Component = tab.Component as React.ComponentType<TProps>;
              return <Component {...(componentProps as TProps & React.Attributes)} />;
            })()}
      </TabsContent>
    );
  });

  if (orientation === 'vertical') {
    return (
      <Tabs className={twMerge('w-full flex-1', className)} orientation="vertical" {...tabsProps}>
        {/* Sticky offset matches AppShell's content padding (p-4 sm:p-8). */}
        <TabsList className="sticky top-4 w-56 shrink-0 self-start sm:top-8">
          {visibleTabTriggers.map((tab) => (
            <Fragment key={tab.tabTrigger.value}>
              {tab.separatorBefore && <Separator className="my-2" />}
              <TabsTrigger disabled={tab.tabTrigger.disabled} value={tab.tabTrigger.value}>
                <TriggerLabel tabTrigger={tab.tabTrigger} />
              </TabsTrigger>
            </Fragment>
          ))}
        </TabsList>
        <FlexContainer className="flex-1" direction="column" items="stretch">
          {headerActions && (
            <FlexContainer className="mb-4 w-full" items="stretch" justify="end">
              {headerActions}
            </FlexContainer>
          )}
          {tabContents}
        </FlexContainer>
      </Tabs>
    );
  }

  return (
    <Tabs className={twMerge('w-full flex-1', className)} {...tabsProps}>
      <FlexContainer className="mb-4 w-full" items="center" justify="between">
        <TabsList className="min-w-0 flex-1 overflow-x-auto">
          {visibleTabTriggers.map((tab) => (
            <TabsTrigger disabled={tab.tabTrigger.disabled} key={tab.tabTrigger.value} value={tab.tabTrigger.value}>
              <TriggerLabel tabTrigger={tab.tabTrigger} />
            </TabsTrigger>
          ))}
        </TabsList>
        {headerActions && <FlexContainer shrink={false}>{headerActions}</FlexContainer>}
      </FlexContainer>
      {tabContents}
    </Tabs>
  );
}
